'use client';

import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fToNow } from '@/utils/format-time';
import type { AgentNotification } from '@/sections/notifications/types';

const categoryIcon = (category: AgentNotification['category']) => {
  if (category === 'ASSIGNMENT') return <AssignmentOutlinedIcon />;
  if (category === 'SCHEDULE_CHANGE') return <ScheduleOutlinedIcon />;
  if (category === 'ACTION_REQUIRED') return <CampaignOutlinedIcon />;
  if (category === 'SAFETY') return <SecurityOutlinedIcon />;
  return <ErrorOutlineRoundedIcon />;
};

export function NotificationItem({
  notification,
  onOpen,
  onArchive,
}: {
  notification: AgentNotification;
  onOpen: () => void;
  onArchive: () => void;
}) {
  return (
    <ListItemButton
      onClick={onOpen}
      sx={{
        py: 1.5,
        px: 2.5,
        alignItems: 'flex-start',
        bgcolor: notification.isUnread ? 'action.hover' : 'transparent',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <ListItemAvatar>
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'grid',
            placeItems: 'center',
            borderRadius: '50%',
            bgcolor: notification.priority === 'URGENT' ? 'error.lighter' : 'primary.lighter',
            color: notification.priority === 'URGENT' ? 'error.main' : 'primary.main',
          }}
        >
          {categoryIcon(notification.category)}
        </Box>
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {notification.title}
            </Typography>
            {notification.isUnread && (
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} />
            )}
          </Stack>
        }
        secondary={
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
              {notification.body}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {fToNow(notification.created)} · {notification.category.replaceAll('_', ' ').toLowerCase()}
            </Typography>
          </Stack>
        }
      />
      <Tooltip title={notification.archivedAt ? 'Restore' : 'Archive'}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onArchive();
          }}
          sx={{ ml: 0.5 }}
        >
          {notification.archivedAt
            ? <UnarchiveOutlinedIcon fontSize="small" />
            : <ArchiveOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
}
